import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectAllAssets } from '../store/slices/assetsSlice';
import TemplateService from '../../../services/TemplateService';
import { useUiContext } from '../../../context/UiContext';
import { useDirtyHelpers } from './useUnsavedChanges';

export default function useSaveTemplate() {
  const navigate = useNavigate();
  const { notifyError, notifySuccess } = useUiContext();
  const { markClean } = useDirtyHelpers();
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  const timelineItems = useSelector((state) => state.timeline.items);
  const allAssets = useSelector(selectAllAssets);

  const saveTemplate = async ({ title, meetingId }) => {
    if (isSavingTemplate) return;
    if (!title || !title.trim()) {
      notifyError('Please enter a template title');
      return;
    }

    const placeholderItems = timelineItems
      .map((item, index) => {
        const asset = allAssets.find((a) => a.id === item.asset_id);
        if (!asset || asset.type !== 'placeholder') return null;
        return {
          id: asset.metadata?.sectionId || undefined,
          title: asset.name,
          description: asset.metadata?.description || '',
          order: index,
        };
      })
      .filter(Boolean);

    if (placeholderItems.length === 0) {
      notifyError('Please add at least one placeholder section');
      return;
    }

    setIsSavingTemplate(true);

    try {
      if (meetingId) {
        await TemplateService.updateTemplate({
          id: meetingId,
          title: title.trim(),
          placeholders: placeholderItems,
        });
        notifySuccess('Template updated successfully');
      } else {
        await TemplateService.createTemplate({
          title: title.trim(),
          placeholders: placeholderItems,
        });
        notifySuccess('Template created successfully');
      }

      markClean();
      // Defer navigation so the navigation blocker unmounts after markClean()
      setTimeout(() => {
        navigate('/templates');
      }, 0);
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || 'Failed to save template';
      notifyError(message);
    } finally {
      setIsSavingTemplate(false);
    }
  };

  return { saveTemplate, isSavingTemplate };
}
